/**
 * COLOR TOKENS
 *
 * Colors are injected by the server as window.__EXT_UI_COLORS__ based on the brand.
 * This file reads from that global, with fallback to extend defaults for SSR/tests.
 *
 * Two-tier architecture:
 * - Base colors: Raw palette values (blue300, grey800, etc.)
 * - Semantic colors: Design tokens that reference base or other semantic colors
 */

import {
  AllColorKey,
  BaseColorKey,
  extendBaseColors,
  extendSemanticColors,
  ResolvedColors,
  SemanticColorKey,
} from 'ext-ui/style/themes/extend/colors'

// =============================================================================
// COLOR RESOLUTION
// =============================================================================

/**
 * Resolve all colors for SSR/fallback (when window global not available)
 */
function resolveExtendDefaults(): ResolvedColors {
  const resolved: Record<string, string> = { ...extendBaseColors }

  // Resolve semantic colors iteratively (can reference base or other semantic)
  const pending = new Map(Object.entries(extendSemanticColors))
  let lastPendingSize = pending.size + 1

  while (pending.size > 0 && pending.size < lastPendingSize) {
    lastPendingSize = pending.size

    for (const [key, ref] of pending) {
      if (resolved[ref]) {
        resolved[key] = resolved[ref]
        pending.delete(key)
      }
    }
  }

  return resolved as ResolvedColors
}

/**
 * Get colors from window global, fallback to resolved extend defaults.
 */
function getColors(): ResolvedColors {
  if (typeof window !== 'undefined' && window.__EXT_UI_COLORS__) {
    return window.__EXT_UI_COLORS__
  }
  return resolveExtendDefaults()
}

// Resolve colors once at module load
const _colors = getColors()

// =============================================================================
// BASE PALETTE COLORS
// =============================================================================

// Blue palette
export const blue100 = _colors.blue100
export const blue150 = _colors.blue150
export const blue200 = _colors.blue200
export const blue300 = _colors.blue300
export const blue400 = _colors.blue400
export const blue450 = _colors.blue450
export const blue500 = _colors.blue500

// Green palette
export const green30 = _colors.green30
export const green50 = _colors.green50
export const _green100 = _colors.green100
export const green150 = _colors.green150
export const green200 = _colors.green200
export const green300 = _colors.green300
export const green400 = _colors.green400
export const green500 = _colors.green500
export const green600 = _colors.green600

// Grey palette
export const grey50 = _colors.grey50
export const grey100 = _colors.grey100
export const grey150 = _colors.grey150
export const grey200 = _colors.grey200
export const grey300 = _colors.grey300
export const grey400 = _colors.grey400
export const grey450 = _colors.grey450
export const grey500 = _colors.grey500
export const grey700 = _colors.grey700
export const grey800 = _colors.grey800

// Purple palette
export const purple100 = _colors.purple100
export const purple200 = _colors.purple200
export const purple250 = _colors.purple250
export const purple300 = _colors.purple300
export const purple400 = _colors.purple400
export const purple500 = _colors.purple500
export const purple600 = _colors.purple600

// Red palette
export const red100 = _colors.red100
export const red200 = _colors.red200
export const red300 = _colors.red300
export const red400 = _colors.red400
export const red500 = _colors.red500
export const red600 = _colors.red600

// White
export const white100 = _colors.white100

// Yellow palette
export const yellow50 = _colors.yellow50
export const _yellow100 = _colors.yellow100
export const yellow200 = _colors.yellow200
export const yellow800 = _colors.yellow800
export const yellow900 = _colors.yellow900

// Neutral
export const neutral600 = _colors.neutral600

// Marketing
export const marketingGreen100 = _colors.marketingGreen100
export const marketingGreen400 = _colors.marketingGreen400

// Misc
export const progressBarBg = _colors.progressBarBg

// =============================================================================
// SEMANTIC COLORS - Core design tokens
// =============================================================================

/** FIGMA: `base/color-action` */
export const base_colorAction = _colors.base_colorAction
/** FIGMA: `base/color-action-hover` */
export const base_colorActionHover = _colors.base_colorActionHover
/** FIGMA: `base/color-new` */
export const base_colorNew = _colors.base_colorNew
/** FIGMA: `base/color-alert` */
export const base_colorAlert = _colors.base_colorAlert
/** FIGMA: `base/color-alert-hover` */
export const base_colorAlertHover = _colors.base_colorAlertHover
/** FIGMA: `base/content-background` */
export const base_contentBackground = _colors.base_contentBackground
/** FIGMA: `base/content-active` */
export const base_contentActive = _colors.base_contentActive
/** FIGMA: `base/content-hover` */
export const base_contentHover = _colors.base_contentHover
/** FIGMA: `base/color-surface` */
export const base_colorSurface = _colors.base_colorSurface
/** FIGMA: `base/color-surface-secondary` */
export const base_colorSurfaceSecondary = _colors.base_colorSurfaceSecondary
/** FIGMA: `base/color-raised-surface` */
export const base_colorRaisedSurface = _colors.base_colorRaisedSurface
/** FIGMA: `base/color-content-border` */
export const base_colorContentBorder = _colors.base_colorContentBorder
/** FIGMA: `base/color-content-border-secondary` */
export const base_colorContentBorderSecondary =
  _colors.base_colorContentBorderSecondary
/** FIGMA: `base/color-gradient-start` */
export const base_colorGradientStart = _colors.base_colorGradientStart
/** FIGMA: `base/color-gradient-end` */
export const base_colorGradientEnd = _colors.base_colorGradientEnd
/** FIGMA: `base/color-highlight` */
export const base_colorHighlight = _colors.base_colorHighlight

// Legacy alias
export const base_contentSelected = base_contentActive

// Gradient semantic colors
/** FIGMA: `gradient/brand-gradient-start` */
export const gradient_brandGradientStart = _colors.gradient_brandGradientStart
/** FIGMA: `gradient/brand-gradient-end` */
export const gradient_brandGradientEnd = _colors.gradient_brandGradientEnd
/** FIGMA: `gradient/bar-chart-gradient-start` */
export const gradient_barChartGradientStart =
  _colors.gradient_barChartGradientStart
/** FIGMA: `gradient/bar-chart-gradient-end` */
export const gradient_barChartGradientEnd = _colors.gradient_barChartGradientEnd
/** FIGMA: `gradient/selected-gradient-start` */
export const gradient_selectedGradientStart =
  _colors.gradient_selectedGradientStart
/** FIGMA: `gradient/selected-gradient-end` */
export const gradient_selectedGradientEnd = _colors.gradient_selectedGradientEnd
/** FIGMA: `gradient/marketing-gradient-start` */
export const gradient_marketingGradientStart =
  _colors.gradient_marketingGradientStart
/** FIGMA: `gradient/marketing-gradient-end` */
export const gradient_marketingGradientEnd =
  _colors.gradient_marketingGradientEnd

// Logo semantic colors
/** FIGMA: `logo/color-logo-primary` */
export const logo_colorLogoPrimary = _colors.logo_colorLogoPrimary
/** FIGMA: `logo/color-logo-secondary` */
export const logo_colorLogoSecondary = _colors.logo_colorLogoSecondary

// Overlay semantic colors
export const overlay_backgroundBase = _colors.overlay_bgColor
export const overlay_backgroundNotFocused = `${_colors.overlay_bgColor}C7`
export const overlay_backgroundFocused = `${_colors.overlay_bgColor}77`

// =============================================================================
// COMPONENT COLORS - Tokens that reference semantic or base colors
// =============================================================================

// Chart colors
/** FIGMA: `charts/bar-chart/bill-pay-default` */
export const charts_barChartBillPayDefault =
  _colors.charts_barChartBillPayDefault
/** FIGMA: `charts/bar-chart/bill-pay-disabled` */
export const charts_barChartBillPayDisabled =
  _colors.charts_barChartBillPayDisabled
/** FIGMA: `charts/bar-chart/virtualCards-default` */
export const charts_barChartVirtualCardsDefault =
  _colors.charts_barChartVirtualCardsDefault
/** FIGMA: `charts/bar-chart/virtualCards-disabled` */
export const charts_barChartVirtualCardsDisabled =
  _colors.charts_barChartVirtualCardsDisabled
/** FIGMA: `charts/bar-chart/physicalCards-default` */
export const charts_barChartPhysicalCardsDefault =
  _colors.charts_barChartPhysicalCardsDefault
/** FIGMA: `charts/bar-chart/physicalCards-disabled` */
export const charts_barChartPhysicalCardsDisabled =
  _colors.charts_barChartPhysicalCardsDisabled

// CTA - Link colors
/** FIGMA: `cta/link-text-primary` */
export const cta_linkTextPrimary = _colors.cta_linkTextPrimary
/** FIGMA: `cta/link-text-primary-hover` */
export const cta_linkTextPrimaryHover = _colors.cta_linkTextPrimaryHover
/** FIGMA: `cta/link-text-alert` */
export const cta_linkTextAlert = _colors.cta_linkTextAlert
/** FIGMA: `cta/link-text-alert-hover` */
export const cta_linkTextAlertHover = _colors.cta_linkTextAlertHover

// CTA - Primary button
/** FIGMA: `cta/button-primary-background` */
export const cta_buttonPrimaryBackground = _colors.cta_buttonPrimaryBackground
/** FIGMA: `cta/button-primary-background-hover` */
export const cta_buttonPrimaryBackgroundHover =
  _colors.cta_buttonPrimaryBackgroundHover
/** FIGMA: `cta/button-primary-border` */
export const cta_buttonPrimaryBorder = _colors.cta_buttonPrimaryBorder
/** FIGMA: `cta/button-primary-text` */
export const cta_buttonPrimaryText = _colors.cta_buttonPrimaryText

// CTA - Secondary button
/** FIGMA: `cta/button-secondary-background` */
export const cta_buttonSecondaryBackground =
  _colors.cta_buttonSecondaryBackground
/** FIGMA: `cta/button-secondary-background-hover` */
export const cta_buttonSecondaryBackgroundHover =
  _colors.cta_buttonSecondaryBackgroundHover
/** FIGMA: `cta/button-secondary-border` */
export const cta_buttonSecondaryBorder = _colors.cta_buttonSecondaryBorder
/** FIGMA: `cta/button-secondary-text` */
export const cta_buttonSecondaryText = _colors.cta_buttonSecondaryText

// CTA - Alert button
/** FIGMA: `cta/alert-button-background` */
export const cta_alertButtonBackground = _colors.cta_alertButtonBackground
/** FIGMA: `cta/alert-button-background-hover` */
export const cta_alertButtonBackgroundHover =
  _colors.cta_alertButtonBackgroundHover
/** FIGMA: `cta/alert-button-border` */
export const cta_alertButtonBorder = _colors.cta_alertButtonBorder
/** FIGMA: `cta/alert-button-secondary-background` */
export const cta_alertButtonSecondaryBackground =
  _colors.cta_alertButtonSecondaryBackground
/** FIGMA: `cta/alert-button-secondary-background-hover` */
export const cta_alertButtonSecondaryBackgroundHover =
  _colors.cta_alertButtonSecondaryBackgroundHover

// CTA - Noshell button
export const cta_noshellButtonBackgroundHover =
  _colors.cta_noshellButtonBackgroundHover
export const cta_noshellButtonAnimatedCircle =
  _colors.cta_noshellButtonAnimatedCircle

// CTA - Spotlight button
export const cta_spotlightButtonBackground =
  _colors.cta_spotlightButtonBackground
export const cta_spotlightBackgroundHover = _colors.cta_spotlightBackgroundHover
export const cta_spotlightButtonBorder = _colors.cta_spotlightButtonBorder

// Input colors
/** FIGMA: `input/background-default` */
export const input_backgroundDefault = _colors.input_backgroundDefault
/** FIGMA: `input/border-default` */
export const input_borderDefault = _colors.input_borderDefault
/** FIGMA: `input/border-hover` */
export const input_borderHover = _colors.input_borderHover
/** FIGMA: `input/border-active` */
export const input_borderActive = _colors.input_borderActive
/** FIGMA: `input/border-error` */
export const input_borderError = _colors.input_borderError

// Navigation colors
/** FIGMA: `nav/nav-item-color-hover` */
export const nav_navItemColorHover = _colors.nav_navItemColorHover
/** FIGMA: `nav/nav-item-color-selected` */
export const nav_navItemColorSelected = _colors.nav_navItemColorSelected
/** FIGMA: `nav/nav-accordion-color-selected` */
export const nav_navAccordionColorSelected =
  _colors.nav_navAccordionColorSelected

// Table colors
/** FIGMA: `table/table-border-color` */
export const table_tableBorderColor = _colors.table_tableBorderColor
/** FIGMA: `table/table-row-selected` */
export const table_tableRowSelected = _colors.table_tableRowSelected
/** FIGMA: `table/table-row-selected-hover` */
export const table_tableRowSelectedHover = _colors.table_tableRowSelectedHover
/** FIGMA: `table/table-row-hover` */
export const table_tableRowHover = _colors.table_tableRowHover

// Tag - Highlight
/** FIGMA: `tag/color-tag-highlight-background` */
export const tag_colorTagHighlightBackground =
  _colors.tag_colorTagHighlightBackground
/** FIGMA: `tag/color-tag-highlight-background-hover` */
export const tag_colorTagHighlightBackgroundHover =
  _colors.tag_colorTagHighlightBackgroundHover
/** FIGMA: `tag/color-tag-highlight-text` */
export const tag_colorTagHighlightText = _colors.tag_colorTagHighlightText
/** FIGMA: `tag/color-tag-highlight-text-hover` */
export const tag_colorTagHighlightTextHover =
  _colors.tag_colorTagHighlightTextHover

// Tag - Default
/** FIGMA: `tag/color-tag-default-background` */
export const tag_colorTagDefaultBackground =
  _colors.tag_colorTagDefaultBackground
/** FIGMA: `tag/color-tag-default-background-hover` */
export const tag_colorTagDefaultBackgroundHover =
  _colors.tag_colorTagDefaultBackgroundHover
/** FIGMA: `tag/color-tag-default-text` */
export const tag_colorTagDefaultText = _colors.tag_colorTagDefaultText
/** FIGMA: `tag/color-tag-default-text-hover` */
export const tag_colorTagDefaultTextHover = _colors.tag_colorTagDefaultTextHover

// Tag - Severe
/** FIGMA: `tag/color-tag-severe-text` */
export const tag_colorTagSevereText = _colors.tag_colorTagSevereText
/** FIGMA: `tag/color-tag-severe-text-hover` */
export const tag_colorTagSevereTextHover = _colors.tag_colorTagSevereTextHover
/** FIGMA: `tag/color-tag-severe-background` */
export const tag_colorTagSevereBackground = _colors.tag_colorTagSevereBackground
/** FIGMA: `tag/color-tag-severe-background-hover` */
export const tag_colorTagSevereBackgroundHover =
  _colors.tag_colorTagSevereBackgroundHover

// Tag - Pending
/** FIGMA: `tag/color-tag-pending-text` */
export const tag_colorTagPendingText = _colors.tag_colorTagPendingText
/** FIGMA: `tag/color-tag-pending-text-hover` */
export const tag_colorTagPendingTextHover = _colors.tag_colorTagPendingTextHover
/** FIGMA: `tag/color-tag-pending-background` */
export const tag_colorTagPendingBackground =
  _colors.tag_colorTagPendingBackground
/** FIGMA: `tag/color-tag-pending-background-hover` */
export const tag_colorTagPendingBackgroundHover =
  _colors.tag_colorTagPendingBackgroundHover

// Tag - New
/** FIGMA: `tag/color-tag-new-text` */
export const tag_colorTagNewText = _colors.tag_colorTagNewText
/** FIGMA: `tag/color-tag-new-text-hover` */
export const tag_colorTagNewTextHover = _colors.tag_colorTagNewTextHover
/** FIGMA: `tag/color-tag-new-background` */
export const tag_colorTagNewBackground = _colors.tag_colorTagNewBackground
/** FIGMA: `tag/color-tag-new-background-hover` */
export const tag_colorTagNewBackgroundHover =
  _colors.tag_colorTagNewBackgroundHover

// Tag - Blue
/** FIGMA: `tag/color-tag-blue-text` */
export const tag_colorTagBlueText = _colors.tag_colorTagBlueText
/** FIGMA: `tag/color-tag-blue-text-hover` */
export const tag_colorTagBlueTextHover = _colors.tag_colorTagBlueTextHover
/** FIGMA: `tag/color-tag-blue-background` */
export const tag_colorTagBlueBackground = _colors.tag_colorTagBlueBackground
/** FIGMA: `tag/color-tag-blue-background-hover` */
export const tag_colorTagBlueBackgroundHover =
  _colors.tag_colorTagBlueBackgroundHover

// Text colors
/** FIGMA: `text/color-text-primary` */
export const text_colorTextPrimary = _colors.text_colorTextPrimary
/** FIGMA: `text/color-text-secondary` */
export const text_colorTextSecondary = _colors.text_colorTextSecondary
/** FIGMA: `text/color-text-tertiary` */
export const text_colorTextTertiary = _colors.text_colorTextTertiary
/** FIGMA: `text/color-text-branded` */
export const text_colorTextBranded = _colors.text_colorTextBranded
/** FIGMA: `text/color-text-accent` */
export const text_colorTextAccent = _colors.text_colorTextAccent

// Notification colors
/** FIGMA: `notifications/color-alert` */
export const notification_colorAlert = _colors.notification_colorAlert

// Graphics colors
/** FIGMA: `graphics/illustration-gradient-primary-start` */
export const graphics_illustrationGradientPrimaryStart =
  _colors.graphics_illustrationGradientPrimaryStart
/** FIGMA: `graphics/illustration-fill-secondary` */
export const graphics_illustrationFillSecondary =
  _colors.graphics_illustrationFillSecondary
/** FIGMA: `graphics/illustration-fill-primary` */
export const graphics_illustrationFillPrimary =
  _colors.graphics_illustrationFillPrimary

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type { ResolvedColors, BaseColorKey, SemanticColorKey, AllColorKey }
